/**
 * Phase 76.3 — Permanent saved Season Report view page.
 *
 * Route: /season-reports/saved/:report_id
 *
 * Loads ONCE from /api/reports/season/saved/{id} and renders the saved
 * snapshot verbatim — never re-fetches live module data. This means the
 * report content is locked at the moment of save, even if upstream stats
 * change later.
 *
 * RBAC is handled server-side:
 *   • Admin / Staff:  see drafts, finalised, archived.
 *   • Player/Parent:  only finalised reports for their own athlete.
 * Server returns 404 if a Player/Parent tries to open a draft.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Loader2, ArrowLeft, Copy, Archive, CheckCircle2, Pencil, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import SeasonReportPrint from './SeasonReportPrint';

const API = process.env.REACT_APP_BACKEND_URL || '';

import { mergeSavedSections } from '../utils/seasonReportSections';

const STATUS_META = {
  draft:     { label: 'Draft',     color: 'bg-amber-100 text-amber-800 border-amber-200',     hint: 'Draft — not visible to parents/players.' },
  finalized: { label: 'Finalized', color: 'bg-emerald-100 text-emerald-800 border-emerald-200', hint: 'Finalized Report.' },
  archived:  { label: 'Archived',  color: 'bg-gray-100 text-gray-700 border-gray-200',          hint: 'Archived — hidden from default lists.' },
};

const SeasonReportView = () => {
  const { report_id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isStaff = user?.role === 'Admin' || user?.role === 'Staff';

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const loadReport = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API}/api/reports/season/saved/${report_id}`, { headers });
      if (res.status === 404) { setError('Report not found or not yet finalised.'); return; }
      if (!res.ok) { setError('Failed to load report.'); return; }
      setReport(await res.json());
    } catch { setError('Failed to load report.'); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report_id]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const updateStatus = async (next) => {
    try {
      const r = await fetch(`${API}/api/reports/season/saved/${report_id}/status`, {
        method: 'PATCH', headers, body: JSON.stringify({ status: next }),
      });
      if (!r.ok) { toast.error('Failed to update status'); return; }
      toast.success(`Report ${next}.`);
      loadReport();
    } catch { toast.error('Failed to update status'); }
  };

  const copyLink = async () => {
    const url = `${window.location.origin}/season-reports/saved/${report_id}`;
    try { await navigator.clipboard.writeText(url); toast.success('Report link copied'); }
    catch { toast.message(url); }
    fetch(`${API}/api/reports/season/saved/${report_id}/link-copied`,
      { method: 'POST', headers }).catch(() => {});
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20" data-testid="saved-report-loading">
          <Loader2 className="animate-spin text-pfap-green" size={28} />
        </div>
      </Layout>
    );
  }

  if (error || !report) {
    return (
      <Layout>
        <Card className="max-w-2xl mx-auto mt-10" data-testid="saved-report-error">
          <CardContent className="py-10 text-center">
            <p className="text-sm text-gray-600 dark:text-gray-300">{error || 'Report not available.'}</p>
            <Button variant="outline" onClick={() => navigate(-1)} className="mt-4">
              <ArrowLeft size={14} className="mr-1" /> Go back
            </Button>
          </CardContent>
        </Card>
      </Layout>
    );
  }

  const status = report.status || 'draft';
  const meta = STATUS_META[status] || STATUS_META.draft;

  // Compose the data shape SeasonReportPrint expects (its `data` prop).
  // Phase 77 — merge legacy `training_attendance` and other pre-registry
  // section keys forward so older saved snapshots still render the
  // canonical 18 sections cleanly.
  const printData = {
    ...(report.snapshot || {}),
    coach_input: report.coach_input || {},
    season: report.season,
    sections: mergeSavedSections(report.snapshot?.sections),
    status,
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Header bar with status badge + action buttons */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4" data-testid="saved-report-header">
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {report.athlete_name || 'Athlete'} — {report.season}
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge className={meta.color} data-testid="saved-report-status-badge">{meta.label}</Badge>
              <span className="text-xs text-gray-500 dark:text-gray-400" data-testid="saved-report-status-hint">
                {meta.hint}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={copyLink} data-testid="saved-report-copy-link">
              <Copy size={14} className="mr-1" /> Copy Link
            </Button>
            {isStaff && status !== 'finalized' && (
              <Button size="sm" className="bg-pfap-green hover:bg-pfap-green/90 text-white"
                      onClick={() => updateStatus('finalized')} data-testid="saved-report-finalize">
                <CheckCircle2 size={14} className="mr-1" /> Finalize
              </Button>
            )}
            {isStaff && status === 'finalized' && (
              <Button size="sm" variant="outline"
                      onClick={() => updateStatus('draft')} data-testid="saved-report-back-to-draft">
                <Pencil size={14} className="mr-1" /> Back to Draft
              </Button>
            )}
            {isStaff && status !== 'archived' && (
              <Button size="sm" variant="outline" onClick={() => updateStatus('archived')} data-testid="saved-report-archive">
                <Archive size={14} className="mr-1" /> Archive
              </Button>
            )}
            {isStaff && (
              <Button size="sm" variant="outline" onClick={() => {
                localStorage.setItem('pfap_season_report', JSON.stringify({ ...printData }));
                window.open('/season-report/print', '_blank');
              }} data-testid="saved-report-open-print">
                <Eye size={14} className="mr-1" /> Open Print View
              </Button>
            )}
          </div>
        </div>

        {/* Embedded print layout (already renders sanitized HTML safely) */}
        <Card data-testid="saved-report-card">
          <CardContent className="p-0">
            <SeasonReportPrint data={printData} embedded />
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default SeasonReportView;
